import { ClockIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { XCircleIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import type { ReactNode } from "react";

import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

import type {
	QuotaListStatus,
	QuotasIndexDefaultPageState,
	QuotasIndexTableData,
} from "./quotasIndexTableTypes.ts";

// A sort order for the status column, draft first.
const quotaStatusOrder: Record<QuotaListStatus, number> = {
	draft: 0,
	active: 1,
	inactive: 2,
};

export const quotaListStatuses: QuotaListStatus[] = ["draft", "active", "inactive"];

export const quotasIndexDefaultPageState: QuotasIndexDefaultPageState = {
	columnFilters: [
		{ id: "status", value: [] },
		{ id: "companyShortName", value: [] },
	],
	globalFilter: "",
	sorting: [{ id: "status", desc: false }],
};

// draft = invoice-backed with no attached invoices; otherwise active / inactive by isBookable.
export const deriveQuotaStatus = (quota: QuotaWithRelations): QuotaListStatus => {
	if (quota.type === "invoice-backed" && quota.invoices.length === 0) {
		return "draft";
	}
	return quota.isBookable ? "active" : "inactive";
};

// The consumed amount as a whole-number percentage of the quota amount. 0 when there is no amount.
export const getConsumedPercent = (amountCents: number, budgetCentsConsumed: number): number => {
	if (!amountCents) {
		return 0;
	}
	return Number(((budgetCentsConsumed / amountCents) * 100).toFixed(1))
};

export const getQuotaStatusDisplayName = (status: QuotaListStatus): string => {
	switch (status) {
		case "draft":
			return "Entwurf";
		case "active":
			return "Aktiv";
		case "inactive":
			return "Inaktiv";
	}
};

// Icons match the invoices index: green euro (active), red X (inactive), draft pencil (draft).
export const getQuotaStatusIcon = (status: QuotaListStatus, className?: string): ReactNode => {
	switch (status) {
		case "draft":
			return <PencilSquareIcon className={clsx("text-primary-300", className)} />;
		case "active":
			return <ClockIcon className={clsx("text-success-500", className)} />;
		case "inactive":
			return <XCircleIcon className={clsx("text-danger-500", className)} />;
	}
};

export const getQuotaStatusOrder = (status: QuotaListStatus): number => quotaStatusOrder[status];

// Flatten one quota for the table, computing the derived fields once.
export const mapQuotaToTableData = (quota: QuotaWithRelations): QuotasIndexTableData => {
	const status = deriveQuotaStatus(quota);
	const managerFullName = quota.manager?.fullName ?? "";
	// An invoice-backed shell has no amount after its last invoice is detached.
	const amountCents = quota.amountCents ?? 0;
	const amountFormatted = formatCentsToCurrency(amountCents);

	return {
		amountCents,
		budgetCentsConsumed: quota.budgetCentsConsumed,
		companyAvatarImage: quota.companyAvatarImage,
		companyId: quota.companyId,
		companyShortName: quota.company?.shortName ?? "",
		consumedPercent: getConsumedPercent(amountCents, quota.budgetCentsConsumed),
		id: quota.id,
		managerFullName,
		// The global search covers title, managedBy name, and the amount.
		searchString: `${quota.title} ${managerFullName} ${amountFormatted}`.toLowerCase(),
		status,
		title: quota.title,
	};
};
